/**
 * Compare two event recaps side by side (R2-07): every performance and
 * assumption field, live values, with the difference. Year-suffixed
 * subsources make "the same event" different subsources ("Woofstock 2025" vs
 * "Woofstock 2026"), so any two instances can be compared; the picker lists
 * same-name events first.
 */
import { AlertCircle, ArrowLeft } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { Link, useSearchParams } from "react-router-dom";

import { EventsNav } from "../../events/EventsNav";
import { ASSUMPTION_FIELDS, PERF_SECTIONS, eventFamily, fmtDelta, fmtValue, perfValue } from "../../events/recapFields";
import { fetchRecapCompare, fetchRecaps } from "../../lib/eventReporting";
import type { RecapDetail, RecapListItem } from "../../lib/eventReporting";

const selCls = "px-3 py-1.5 rounded-md border hairline text-[13.5px] focus-ring w-full";

export function EventCompare() {
  const [params, setParams] = useSearchParams();
  const a = params.get("a") ?? "";
  const b = params.get("b") ?? "";
  const [list, setList] = useState<RecapListItem[]>([]);
  const [pair, setPair] = useState<{ a: RecapDetail; b: RecapDetail } | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { fetchRecaps().then(setList).catch((e: Error) => setError(e.message)); }, []);
  useEffect(() => {
    setPair(null);
    if (a && b) fetchRecapCompare(a, b).then(setPair).catch((e: Error) => setError(e.message));
  }, [a, b]);

  const suggestions = useMemo(() => {
    const anchor = list.find((x) => x.instance_id === (a || b));
    if (!anchor) return [];
    const fam = eventFamily(anchor.subsource);
    return list.filter((x) => x.instance_id !== anchor.instance_id && eventFamily(x.subsource) === fam);
  }, [list, a, b]);

  function setSide(side: "a" | "b", id: string) {
    const next = new URLSearchParams(params);
    if (id) next.set(side, id); else next.delete(side);
    setParams(next, { replace: true });
  }

  const [A, B] = pair ? [pair.a, pair.b] : [null, null];

  return (
    <div className="p-8 view-in overflow-y-auto h-full scroll-quiet">
      <header className="mb-4">
        <Link to="/events" className="inline-flex items-center gap-1 text-[13px] mb-2 focus-ring rounded-sm" style={{ color: "var(--ink-3)" }}>
          <ArrowLeft size={13} /> Event Recaps
        </Link>
        <h1 className="font-serif text-[28px] leading-tight" style={{ color: "var(--ink)" }}>Compare events</h1>
      </header>

      <EventsNav />

      <div className="grid md:grid-cols-2 gap-3 max-w-3xl mb-3">
        {(["a", "b"] as const).map((side) => (
          <label key={side} className="block">
            <div className="text-[10.5px] font-mono uppercase tracking-[0.14em] mb-1" style={{ color: "var(--ink-3)" }}>
              Event {side.toUpperCase()}
            </div>
            <select value={side === "a" ? a : b} onChange={(e) => setSide(side, e.target.value)} className={selCls}
              style={{ background: "var(--bg-elev)", color: "var(--ink)" }}>
              <option value="">Choose an event…</option>
              {list.map((x) => <option key={x.instance_id} value={x.instance_id}>{x.display_name}</option>)}
            </select>
          </label>
        ))}
      </div>
      {!(a && b) && suggestions.length > 0 && (
        <p className="text-[13px] mb-4" style={{ color: "var(--ink-3)" }}>
          Same event, other years:{" "}
          {suggestions.map((x, i) => (
            <span key={x.instance_id}>
              {i > 0 && ", "}
              <button type="button" className="underline focus-ring rounded-sm" style={{ color: "var(--ink)" }}
                onClick={() => setSide(a ? "b" : "a", x.instance_id)}>{x.display_name}</button>
            </span>
          ))}
        </p>
      )}

      {error && (
        <div className="rounded-md p-4 mb-4 font-mono text-[13px] flex items-start gap-2"
          style={{ background: "var(--oxblood-bg)", color: "var(--oxblood)", border: "1px solid var(--oxblood)" }}>
          <AlertCircle size={14} className="mt-0.5 shrink-0" /> <span>{error}</span>
        </div>
      )}

      {A && B && (
        <section className="rounded-lg border hairline overflow-hidden max-w-5xl" style={{ background: "var(--bg-elev)" }}>
          <table className="w-full text-[13px]">
            <thead>
              <tr className="text-[10.5px] font-mono uppercase tracking-[0.14em] border-b hairline" style={{ color: "var(--ink-3)" }}>
                <th className="px-5 py-2 font-normal text-left">Metric</th>
                <th className="px-3 py-2 font-normal text-right normal-case tracking-normal font-serif text-[13px]" style={{ color: "var(--ink)" }}>
                  <Link to={`/events/${A.recap.instance_id}`} className="hover:underline">{A.recap.display_name}</Link>
                </th>
                <th className="px-3 py-2 font-normal text-right normal-case tracking-normal font-serif text-[13px]" style={{ color: "var(--ink)" }}>
                  <Link to={`/events/${B.recap.instance_id}`} className="hover:underline">{B.recap.display_name}</Link>
                </th>
                <th className="px-5 py-2 font-normal text-right">B − A</th>
              </tr>
            </thead>
            <tbody>
              {PERF_SECTIONS.map((s) => (
                <Rows key={s.title} title={s.title}>
                  {s.rows.map((p) => {
                    const va = perfValue(A.recap, p.key), vb = perfValue(B.recap, p.key);
                    return (
                      <tr key={p.key} className="border-b hairline" style={{ color: "var(--ink)", fontWeight: p.total ? 600 : 400 }}>
                        <td className="px-5 py-1.5">{p.label}</td>
                        <td className="px-3 py-1.5 text-right font-mono">{fmtValue(va, p.fmt)}</td>
                        <td className="px-3 py-1.5 text-right font-mono">{fmtValue(vb, p.fmt)}</td>
                        <td className="px-5 py-1.5 text-right font-mono" style={{ color: "var(--ink-3)" }}>{fmtDelta(vb, va, p.fmt)}</td>
                      </tr>
                    );
                  })}
                </Rows>
              ))}
              <Rows title="Assumptions">
                {ASSUMPTION_FIELDS.map((f) => {
                  const va = A.assumptions?.[f.key] ?? null, vb = B.assumptions?.[f.key] ?? null;
                  const numeric = f.kind === "int" || f.kind === "money";
                  const show = (v: string | number | null) =>
                    v == null ? "–" : numeric ? fmtValue(Number(v), f.kind === "money" ? "money" : "int") : String(v);
                  return (
                    <tr key={f.key} className="border-b hairline last:border-0" style={{ color: "var(--ink)" }}>
                      <td className="px-5 py-1.5">{f.label}</td>
                      <td className="px-3 py-1.5 text-right">{show(va)}</td>
                      <td className="px-3 py-1.5 text-right">{show(vb)}</td>
                      <td className="px-5 py-1.5 text-right font-mono" style={{ color: "var(--ink-3)" }}>
                        {numeric && va != null && vb != null ? fmtDelta(Number(vb), Number(va), f.kind === "money" ? "money" : "int") : ""}
                      </td>
                    </tr>
                  );
                })}
              </Rows>
            </tbody>
          </table>
        </section>
      )}
    </div>
  );
}

function Rows({ title, children }: { title: string; children: ReactNode }) {
  return (
    <>
      <tr>
        <td colSpan={4} className="px-5 pt-3 pb-1 text-[10.5px] font-mono uppercase tracking-[0.14em]" style={{ color: "var(--ink-3)" }}>{title}</td>
      </tr>
      {children}
    </>
  );
}
